import React, { useState, useEffect } from 'react';
import { Search, ShieldAlert, Award, Star, Ban, Unlock, RefreshCw, XCircle } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const SellersManagement = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Confirmation Modal State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ id: null, status: null, title: '' });

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/sellers`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setSellers(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/status`, { status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setIsConfirmOpen(false);
    }
  };

  const openConfirmModal = (id, status, title) => {
    setConfirmAction({ id, status, title });
    setIsConfirmOpen(true);
  };

  const handleUpdateLevel = async (id, level) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/level`, { level }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to update level");
    }
  };

  const handleToggleFreeze = async (id, currentFreeze) => {
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/sellers/${id}/freeze`, { freeze: !currentFreeze }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        fetchSellers();
      }
    } catch (err) {
      alert("Failed to toggle freeze state");
    }
  };

  const filteredSellers = sellers.filter(seller => 
    (seller.first_name && seller.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.last_name && seller.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (seller.email && seller.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Sellers Management</h2>
          <p className="text-slate-500 mt-1">Approve pending applications, manage seller tiers (Bronze, Silver, Gold, VIP), and track performance.</p>
        </div>
        <button 
          onClick={fetchSellers}
          className="p-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 active:scale-95 transition shadow-sm flex items-center justify-center cursor-pointer"
          title="Refresh Data"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Search */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search sellers..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading sellers directory...</p>
          </div>
        ) : filteredSellers.length === 0 ? (
          <div className="p-20 text-center text-slate-400">
            <Award size={48} className="mx-auto mb-4 opacity-30" />
            <p>No coin sellers found in system.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Seller Details</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Seller Tier</th>
                  <th className="p-6">Commission</th>
                  <th className="p-6">Freeze State</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredSellers.map((seller) => (
                  <tr key={seller.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold">
                          {seller.first_name?.[0]}{seller.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{seller.first_name} {seller.last_name}</p>
                          <p className="text-[10px] text-slate-400">ID: {seller.id.substring(0, 8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-600">
                      <p>{seller.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">WhatsApp: {seller.whatsapp_number || 'N/A'}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {seller.country === 'India' ? 'Aadhar: ' : 'ID/Passport: '} 
                        {seller.aadhar_card || 'N/A'}
                      </p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 px-2 py-0.5 bg-slate-100 rounded-md inline-block">
                        {seller.country || 'N/A'}
                      </p>
                    </td>
                    <td className="p-6">
                      <select 
                        value={seller.level} 
                        onChange={(e) => handleUpdateLevel(seller.id, e.target.value)}
                        className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-pink-500/20"
                      >
                        <option value="BRONZE">BRONZE</option>
                        <option value="SILVER">SILVER</option>
                        <option value="GOLD">GOLD</option>
                        <option value="VIP">VIP</option>
                      </select>
                    </td>
                    <td className="p-6 font-bold text-slate-700">{seller.commission_percentage}%</td>
                    <td className="p-6">
                      <button
                        onClick={() => handleToggleFreeze(seller.id, seller.is_frozen)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase transition ${
                          seller.is_frozen ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'
                        }`}
                      >
                        {seller.is_frozen ? <Ban size={12} /> : <Unlock size={12} />}
                        {seller.is_frozen ? 'Frozen' : 'Active'}
                      </button>
                    </td>
                    <td className="p-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        seller.status === 'approved' ? 'bg-green-100 text-green-700' :
                        seller.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {seller.status}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-2">
                        {seller.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => openConfirmModal(seller.id, 'approved', 'Approve this seller?')}
                              className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 active:scale-95 transition cursor-pointer"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => openConfirmModal(seller.id, 'rejected', 'Reject this seller?')}
                              className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 active:scale-95 transition cursor-pointer"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {seller.status === 'approved' && (
                          <button 
                            onClick={() => handleUpdateStatus(seller.id, 'blocked')}
                            className="px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 active:scale-95 transition"
                          >
                            Block Seller
                          </button>
                        )}
                        {seller.status === 'blocked' && (
                          <button 
                            onClick={() => handleUpdateStatus(seller.id, 'approved')}
                            className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 active:scale-95 transition"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm p-6 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">{confirmAction.title}</h3>
            <p className="text-slate-500 text-sm mb-6">Are you sure you want to proceed with this action? This cannot be undone immediately.</p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => setIsConfirmOpen(false)}
                className="px-6 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleUpdateStatus(confirmAction.id, confirmAction.status)}
                className="px-6 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition active:scale-95 cursor-pointer"
              >
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellersManagement;
